import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { AlertCircle, X, Wind, Hand, Sparkles, Phone } from 'lucide-react';

const EmergencyCalmButton = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [activeExercise, setActiveExercise] = useState(null);
  const [breathCount, setBreathCount] = useState(0);
  const [breathPhase, setBreathPhase] = useState('ready'); // ready, inhale, hold, exhale
  const [affirmationIndex, setAffirmationIndex] = useState(0);
  
  // Don't show on login/signup pages
  const publicPages = ['/login', '/signup'];
  if (publicPages.includes(location.pathname)) {
    return null;
  }

  const affirmations = [
    "You are safe right now. This moment will pass.",
    "Your feelings are valid. You are not alone.",
    "Breathe. You've survived 100% of your worst days.",
    "This anxiety does not define you. You are stronger than this feeling.",
    "You are grounded. You are here. You are okay.",
    "Your calm is coming back to you with each breath.",
    "You have the strength to get through this moment.",
    "This feeling is temporary. Peace is returning to you."
  ];

  const crisisContacts = [
    { name: "National Crisis Hotline", number: "988", description: "24/7 Crisis Support" },
    { name: "Crisis Text Line", number: "Text HOME to 741741", description: "24/7 Text Support" },
    { name: "National Domestic Violence Hotline", number: "1-800-799-7233", description: "24/7 Support" }
  ];

  // Breathing exercise timer (4-7-8 technique)
  useEffect(() => {
    if (activeExercise !== 'breathing' || breathPhase === 'ready') return;

    let timer;
    if (breathPhase === 'inhale') {
      timer = setTimeout(() => setBreathPhase('hold'), 4000);
    } else if (breathPhase === 'hold') {
      timer = setTimeout(() => setBreathPhase('exhale'), 7000);
    } else if (breathPhase === 'exhale') {
      timer = setTimeout(() => {
        setBreathCount(prev => prev + 1);
        setBreathPhase('inhale');
      }, 8000);
    }

    return () => clearTimeout(timer);
  }, [breathPhase, activeExercise]);

  const startBreathingExercise = () => {
    setActiveExercise('breathing');
    setBreathPhase('inhale');
    setBreathCount(0);
  };

  const getBreathingInstructions = () => {
    switch (breathPhase) {
      case 'ready':
        return 'Ready to begin? Take a comfortable position.';
      case 'inhale':
        return 'Breathe in slowly through your nose...';
      case 'hold':
        return 'Hold your breath gently...';
      case 'exhale':
        return 'Breathe out slowly through your mouth...';
      default:
        return '';
    }
  };

  const getBreathingCircleSize = () => {
    switch (breathPhase) {
      case 'inhale':
        return 'scale-125';  // Smaller expansion so it doesn't cover text
      case 'hold':
        return 'scale-125';   // Keep it at the same size
      case 'exhale':
        return 'scale-100';
      default:
        return 'scale-100';
    }
  };

  const groundingSteps = [
    { number: 5, instruction: "Name 5 things you can see around you" },
    { number: 4, instruction: "Name 4 things you can touch" },
    { number: 3, instruction: "Name 3 things you can hear" },
    { number: 2, instruction: "Name 2 things you can smell" },
    { number: 1, instruction: "Name 1 thing you can taste" }
  ];

  return (
    <>
      {/* Floating Emergency Calm Button */}
      <div className="fixed bottom-6 right-6 z-50 group">
        <Button
          onClick={() => setIsOpen(true)}
          className="h-16 w-16 rounded-full bg-gradient-to-br from-orange-400 to-orange-500 hover:from-orange-500 hover:to-orange-600 shadow-2xl flex items-center justify-center animate-pulse relative"
          title="Need to Breathe?"
        >
          <Wind className="w-8 h-8 text-white" />
        </Button>
        
        {/* Tooltip */}
        <div className="absolute bottom-full mb-3 right-0 bg-[#3E324D] text-white text-sm px-4 py-2 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none shadow-xl">
          <p className="font-semibold">Need to Calm Down?</p>
          <p className="text-xs text-[#C8B5E0]">Breathing exercise & grounding</p>
        </div>
      </div>

      {/* Emergency Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-[#2E2536] rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-[#2E2536] border-b border-[#C8B5E0]/20 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#EDE8F3]">You're Safe Here</h2>
              <Button
                onClick={() => {
                  setIsOpen(false);
                  setActiveExercise(null);
                  setBreathPhase('ready');
                }}
                variant="ghost"
                size="icon"
                className="text-[#EDE8F3] hover:bg-[#C8B5E0]/20 rounded-full"
              >
                <X className="w-6 h-6" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Main Menu */}
              {!activeExercise && (
                <>
                  <p className="text-[#C8B5E0] text-center mb-8">
                    Take a moment. You're going to be okay. Choose what feels right for you right now.
                  </p>

                  <div className="grid gap-4">
                    {/* Breathing Exercise */}
                    <button
                      onClick={startBreathingExercise}
                      className="bg-[#3E324D] hover:bg-[#4A3B5A] rounded-xl p-6 text-left transition-all group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-full bg-[#6C4CA2]/20 group-hover:bg-[#6C4CA2]/30 transition-colors">
                          <Wind className="w-6 h-6 text-[#6C4CA2]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#EDE8F3] mb-1">Breathing Exercise</h3>
                          <p className="text-sm text-[#C8B5E0]">4-7-8 breathing technique to calm your nervous system</p>
                        </div>
                      </div>
                    </button>

                    {/* Grounding Exercise */}
                    <button
                      onClick={() => setActiveExercise('grounding')}
                      className="bg-[#3E324D] hover:bg-[#4A3B5A] rounded-xl p-6 text-left transition-all group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-full bg-[#AAB49F]/20 group-hover:bg-[#AAB49F]/30 transition-colors">
                          <Hand className="w-6 h-6 text-[#AAB49F]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#EDE8F3] mb-1">5-4-3-2-1 Grounding</h3>
                          <p className="text-sm text-[#C8B5E0]">Use your senses to anchor yourself to the present moment</p>
                        </div>
                      </div>
                    </button>

                    {/* Affirmations */}
                    <button
                      onClick={() => setActiveExercise('affirmations')}
                      className="bg-[#3E324D] hover:bg-[#4A3B5A] rounded-xl p-6 text-left transition-all group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-full bg-[#E7C6C2]/20 group-hover:bg-[#E7C6C2]/30 transition-colors">
                          <Sparkles className="w-6 h-6 text-[#E7C6C2]" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#EDE8F3] mb-1">Calming Affirmations</h3>
                          <p className="text-sm text-[#C8B5E0]">Gentle reminders that you are safe and supported</p>
                        </div>
                      </div>
                    </button>

                    {/* Crisis Contacts */}
                    <button
                      onClick={() => setActiveExercise('crisis')}
                      className="bg-[#3E324D] hover:bg-[#4A3B5A] rounded-xl p-6 text-left transition-all group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-3 rounded-full bg-red-500/20 group-hover:bg-red-500/30 transition-colors">
                          <Phone className="w-6 h-6 text-red-400" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-[#EDE8F3] mb-1">Crisis Support</h3>
                          <p className="text-sm text-[#C8B5E0]">Immediate help from trained professionals (24/7)</p>
                        </div>
                      </div>
                    </button>
                  </div>
                </>
              )}

              {/* Breathing Exercise */}
              {activeExercise === 'breathing' && (
                <div className="text-center py-8">
                  <Button
                    onClick={() => setActiveExercise(null)}
                    variant="ghost"
                    className="mb-8 text-[#C8B5E0]"
                  >
                    ← Back to menu
                  </Button>

                  {/* Instructions at top - above circle */}
                  <div className="mb-6">
                    <p className="text-2xl font-semibold text-[#EDE8F3] mb-2">
                      {getBreathingInstructions()}
                    </p>
                    <p className="text-[#C8B5E0]">
                      Cycle {breathCount + 1} • Continue for 3-5 cycles
                    </p>
                  </div>

                  {/* Breathing Circle */}
                  <div className="py-8">
                    <div className="relative w-40 h-40 mx-auto">
                      <div 
                        className={`absolute inset-0 rounded-full bg-gradient-to-br from-[#6C4CA2] to-[#8B6BB8] transition-transform duration-[4000ms] ease-in-out ${getBreathingCircleSize()}`}
                      />
                    </div>
                  </div>

                  {breathPhase === 'ready' && (
                    <Button
                      onClick={() => setBreathPhase('inhale')}
                      className="bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-8 py-3 rounded-full"
                    >
                      Start Breathing
                    </Button>
                  )}
                </div>
              )}

              {/* Grounding Exercise */}
              {activeExercise === 'grounding' && (
                <div className="py-4">
                  <Button
                    onClick={() => setActiveExercise(null)}
                    variant="ghost"
                    className="mb-6 text-[#C8B5E0]"
                  >
                    ← Back to menu
                  </Button>

                  <h3 className="text-xl font-semibold text-[#EDE8F3] mb-6 text-center">
                    5-4-3-2-1 Grounding Technique
                  </h3>

                  <p className="text-[#C8B5E0] mb-8 text-center">
                    Slowly work through each step. Take your time with each one.
                  </p>

                  <div className="space-y-4">
                    {groundingSteps.map((step) => (
                      <div key={step.number} className="bg-[#3E324D] rounded-xl p-6">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-[#AAB49F] flex items-center justify-center flex-shrink-0">
                            <span className="text-2xl font-bold text-white">{step.number}</span>
                          </div>
                          <p className="text-[#EDE8F3] text-lg">{step.instruction}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="text-[#C8B5E0] mt-6 text-center text-sm">
                    Take deep breaths between each step. You're doing great.
                  </p>
                </div>
              )}

              {/* Affirmations */}
              {activeExercise === 'affirmations' && (
                <div className="py-8">
                  <Button
                    onClick={() => setActiveExercise(null)}
                    variant="ghost"
                    className="mb-6 text-[#C8B5E0]"
                  >
                    ← Back to menu
                  </Button>

                  <div className="bg-gradient-to-br from-[#E7C6C2] to-[#F7E5E5] rounded-2xl p-8 mb-6">
                    <p className="text-2xl font-semibold text-[#3E324D] text-center leading-relaxed">
                      "{affirmations[affirmationIndex]}"
                    </p>
                  </div>

                  <div className="flex justify-center gap-4">
                    <Button
                      onClick={() => setAffirmationIndex((prev) => (prev - 1 + affirmations.length) % affirmations.length)}
                      className="bg-[#3E324D] hover:bg-[#4A3B5A] text-[#EDE8F3] px-6 py-3 rounded-full"
                    >
                      Previous
                    </Button>
                    <Button
                      onClick={() => setAffirmationIndex((prev) => (prev + 1) % affirmations.length)}
                      className="bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-6 py-3 rounded-full"
                    >
                      Next
                    </Button>
                  </div>

                  <p className="text-center text-[#C8B5E0] mt-6 text-sm">
                    {affirmationIndex + 1} of {affirmations.length}
                  </p>
                </div>
              )}

              {/* Crisis Contacts */}
              {activeExercise === 'crisis' && (
                <div className="py-4">
                  <Button
                    onClick={() => setActiveExercise(null)}
                    variant="ghost"
                    className="mb-6 text-[#C8B5E0]"
                  >
                    ← Back to menu
                  </Button>

                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 mb-6">
                    <p className="text-[#EDE8F3] text-center">
                      If you're in immediate danger, please call <strong>911</strong> or go to your nearest emergency room.
                    </p>
                  </div>

                  <h3 className="text-xl font-semibold text-[#EDE8F3] mb-6">
                    24/7 Crisis Support Resources
                  </h3>

                  <div className="space-y-4">
                    {crisisContacts.map((contact, index) => (
                      <div key={index} className="bg-[#3E324D] rounded-xl p-6">
                        <h4 className="text-lg font-semibold text-[#EDE8F3] mb-1">
                          {contact.name}
                        </h4>
                        <p className="text-sm text-[#C8B5E0] mb-3">
                          {contact.description}
                        </p>
                        <a
                          href={`tel:${contact.number.replace(/[^0-9]/g, '')}`}
                          className="inline-flex items-center gap-2 bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white px-4 py-2 rounded-full text-sm transition-colors"
                        >
                          <Phone className="w-4 h-4" />
                          {contact.number}
                        </a>
                      </div>
                    ))}
                  </div>

                  <p className="text-[#C8B5E0] mt-6 text-center text-sm">
                    You are not alone. Help is always available.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmergencyCalmButton;
